import { useContext } from "react";
import { LuCircleAlert as LuAlertCircle } from "react-icons/lu";

import DesoAvatar from "@/components/shared/deso-avatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { DESO_APP_NAME } from "@/utils/constants";
import { cn } from "@/utils/lib";
import { getUserDisplayName } from "@/utils/usernames";

export enum PendingPostStatus {
  Pending,
  Error,
}

export type PendingPost = {
  id: string;
  body: string;
  status: PendingPostStatus;
  onRetry: () => void;
  onCancel: () => void;
};

export const FeedPendingPostItem = ({
  body,
  status = PendingPostStatus.Error,
  onRetry,
  onCancel,
}: PendingPost) => {
  const { user } = useContext(UserContext);
  const message =
    status === PendingPostStatus.Pending
      ? `Committing your post to the ${DESO_APP_NAME} blockchain...`
      : "Ooops...";

  return (
    <div
      className={cn(
        `
          flex flex-row gap-4 border-t border-border-light bg-accent p-4
          md:p-6
        `,
        {
          "animate-pulse": status === PendingPostStatus.Pending,
        },
      )}
    >
      <div>
        <DesoAvatar account={user} size={56} />
      </div>
      <div
        className="
          flex max-w-[calc(100%-56px-1.5rem)] basis-full flex-col gap-4
        "
      >
        <div className="flex">
          <div className="flex flex-col gap-1">
            <h2 className="flex text-base font-semibold text-muted-foreground">
              {getUserDisplayName({
                extraData: user?.extraData,
                publicKey: user?.publicKey,
                username: user?.username,
              })}
            </h2>
            <div className={"place-self-start text-sm text-muted"}>
              {message}
            </div>
          </div>
          <div className="ml-auto">
            {status === PendingPostStatus.Pending && (
              <Badge variant="default">Pending</Badge>
            )}
            {status === PendingPostStatus.Error && (
              <Badge variant="destructive">Failed</Badge>
            )}
          </div>
        </div>
        <p
          className={"overflow-hidden text-sm whitespace-nowrap"}
          style={{
            WebkitMaskImage:
              "linear-gradient(90deg, #000000FF 20%, #00000000 100%)",
          }}
        >
          {body}
        </p>
        {status === PendingPostStatus.Error && (
          <Alert variant="destructive" className="w-full">
            <LuAlertCircle />
            <AlertTitle>Failed to submit your post</AlertTitle>
            <AlertDescription>
              You may{" "}
              <Button className="h-auto p-0" variant="link" onClick={onRetry}>
                Edit your post
              </Button>{" "}
              and try again, or{" "}
              <Button className="h-auto p-0" variant="link" onClick={onCancel}>
                Delete
              </Button>{" "}
              your content.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};
